<script>
export default {
    name: "CardInfoBLCapture",
    props: {
        dossier: {
            type: Object,
            required: true
        }
    },
    methods: {
        // Le rendu canvas d'html2canvas mesure parfois l'espace en bordure de
        // mot a largeur nulle et colle les mots entre eux ("NUMERO BL" ->
        // "NUMEROBL"). Le "_" est un caractere visible, jamais mesure a
        // largeur nulle : on l'utilise comme separateur pour toutes les
        // valeurs affichees dans la carte de capture.
        sep(value) {
            if (value == null) return "";
            return String(value).trim().replace(/\s+/g, '_');
        },
        formatNumber(value) {
            if (value == null) return "";
            return new Intl.NumberFormat("fr-FR").format(value);
        },
        getQteClass(qte) {
            return qte > 0 ? 'qte-restant' : 'qte-cloturee';
        },
        getStatusClass(dossier) {
            if (dossier.etat_dossier > 0) return 'is_green';

            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const dateArrivee = new Date(dossier.ouverture_date_arriver);

            if (dateArrivee > today) return 'is_pending';
            return 'is_en_cours';
        },
        getPlusInfo(dossier) {
            return (dossier.dossier_info_plus && dossier.dossier_info_plus.length > 0)
                ? dossier.dossier_info_plus[0]
                : {};
        },
        getStatutClass(statut) {
            return statut ? 'statut-badge-ok' : 'statut-badge-vide';
        }
    }
}
</script>

<template>
    <div class="capture-card"
         :class="getStatusClass(dossier)"
    >
        <div class="capture-header">
            <div class="dossier-number">
                {{ sep(dossier.nom_client) }}
            </div>
            <div class="status-badge">
                REP: {{ sep(dossier.numero_repertoire) }}
            </div>
        </div>

        <div class="dossier-info">
            <div class="dossier-details">
                <div class="detail-row">
                    <span class="detail-label">NUMERO_BL:</span>
                    <span class="detail-value">{{ sep(dossier.ouverture_bl_numero) }}</span>
                </div>
                <div class="detail-row">
                    <span class="detail-label">NUMÉRO_DE_FACTURE:</span>
                    <span class="detail-value"> {{ sep(dossier.ouverture_invoice) }} </span>
                </div>
                <div class="detail-row">
                    <span class="detail-label">DATE_DE_RÉCEPTION_DES_DOCUMENTS:</span>
                    <span class="detail-value"> {{ sep(dossier.ouverture_date_dossier_formatted) }}</span>
                </div>
                <div class="detail-row">
                    <span class="detail-label">DATE_D'ARRIVÉE_PRÉVUE_DU_NAVIRE:</span>
                    <span class="detail-value"> {{ sep(dossier.ouverture_date_arriver_formatted) }}</span>
                </div>
                <div class="detail-row">
                    <span class="detail-label">NOM_DU_NAVIRE:</span>
                    <span class="detail-value"> {{ sep(dossier.ouverture_navire) }}</span>
                </div>
                <div class="detail-row">
                    <span class="detail-label">MARCHANDISES:</span>
                    <span class="detail-value"> {{ sep(dossier.ouverture_articles) }}</span>
                </div>
                <div class="detail-row">
                    <span class="detail-label">POIDS:</span>
                    <span class="detail-value"> {{ sep(dossier.ouverture_poids_net) }}</span>
                </div>
                <div class="detail-row">
                    <span class="detail-label">QUANTITÉ_DE_CONTENEURS:</span>
                    <span class="detail-value text-bold">{{ sep(dossier.total_tc) }}</span>
                </div>
                <div class="detail-row">
                    <span class="detail-label">TYPE:</span>
                    <span class="detail-value text-bold">{{ sep(dossier.type_container) }}</span>
                </div>
                <div class="detail-row">
                    <span class="detail-label">FRANCHISE_MICTSL:</span>
                    <span class="detail-value text-bold">{{ sep(getPlusInfo(dossier).franchise_mictsl) }}</span>
                </div>
                <div class="detail-row">
                    <span class="detail-label">DATE_LIMITE_MICTSL:</span>
                    <span class="detail-value text-bold">{{ sep(getPlusInfo(dossier).date_limit_mictsl_formatted) }}</span>
                </div>
                <div class="detail-row">
                    <span class="detail-label">FRANCHISE_COMPAGNIE:</span>
                    <span class="detail-value text-bold">{{ sep(getPlusInfo(dossier).franchise_compagnie) }}</span>
                </div>
                <div class="detail-row">
                    <span class="detail-label">DATE_LIMITE:</span>
                    <span class="detail-value text-bold">{{ sep(getPlusInfo(dossier).date_limite_2_formatted) }}</span>
                </div>
            </div>
        </div>

        <div :class="dossier.articles.length > 0 ? 'list_article' : 'list_article_not_border'">
            <div v-for="(article, index_article) in dossier.articles" :key="index_article">
                <div class="article-label">
                    {{ sep(article.reference_article) }}
                </div>
                <div :class="getQteClass(article.qte_restant)">
                    <i :class="article.qte_restant == 0 ? 'fa fa-check-circle' : ''"></i>
                    {{ article.qte_restant != 0 ? sep(formatNumber(article.qte_restant)) : '' }}
                </div>
            </div>
        </div>

        <div :class="dossier.document_associer.length > 0 ? 'list_article' : 'list_article_not_border'">
            <table class="table table-striped table-bordered">
                <thead>
                    <tr>
                        <th>Document</th>
                        <th>Statut</th>
                        <th>Date</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(doc, index) in dossier.document_associer" :key="index">
                        <td>{{ sep(doc.nom_document) }}</td>
                        <td>
                            <span class="statut-badge" :class="getStatutClass(doc.statut)">
                                <i :class="doc.statut ? 'fa fa-check-circle' : 'fa fa-exclamation-circle'"></i>
                                {{ sep(doc.statut ? doc.statut : 'Manquant') }}
                            </span>
                        </td>
                        <td>{{ sep(doc.date_reception_formatted) }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<style scoped>
/* Format large et fixe dedie a la capture d'image : evite le retrecissement
   du texte qui survenait dans la grille responsive (largeur mini 300px). */
.capture-card {
    width: 700px;
    background: white;
    border-radius: 15px;
    padding: 30px;
    border: 1px solid #e9ecef;
    position: relative;
    overflow: hidden;
    font-family: inherit;
}

.capture-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #076360, #0a7a77);
}

.capture-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.dossier-number {
    font-size: 20px;
    font-weight: 700;
    color: #076360;
}

.status-badge {
    padding: 5px 14px;
    border-radius: 15px;
    font-size: 15px;
    color: #3a2626;
    background-color: #f8f9fa;
    font-weight: bold !important;
    white-space: nowrap;
    /* Le rendu "canvas" d'html2canvas (foreignObjectRendering desactive) ne
       supporte pas la propriete CSS "gap" en flex/grid : marge explicite
       a la place pour garantir l'espacement dans l'image capturee. */
    margin-left: 12px;
}

.dossier-details {
    border-top: 1px solid #f0f0f0;
    padding-top: 18px;
    margin-top: 18px;
}

.detail-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
    font-size: 16px;
}

.detail-label {
    color: #6c757d;
    font-weight: 600;
    padding-right: 20px;
}

.detail-value {
    color: #212529;
    font-weight: 500;
    text-align: right;
}

.text-bold {
    font-weight: 700 !important;
}

.list_article_not_border {
    padding-top: 8px;
    margin-top: 8px;
}

.list_article {
    border-top: 2px solid #e9ecef;
    padding-top: 8px;
    margin-top: 8px;
}

.list_article > div {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 5px 0;
    font-size: 16px;
}

.article-label {
    color: #343a40;
    font-weight: 500;
    flex-grow: 1;
    padding-right: 15px;
}

.qte-restant, .qte-cloturee {
    font-weight: 700;
    min-width: 40px;
    text-align: right;
    padding: 3px 10px;
    border-radius: 4px;
    white-space: nowrap;
}

.qte-restant {
    color: #dc3545;
    background-color: #f8d7da;
    border: 1px solid #f5c6cb;
}
.qte-restant .fa {
    color: #dc3545;
    margin-right: 5px;
}

.qte-cloturee {
    color: #155724;
    background-color: #d4edda;
    border: 1px solid #c3e6cb;
}
.qte-cloturee .fa {
    color: #155724;
    margin-right: 5px;
}

.table {
    width: 100%;
    font-size: 15px;
}

.statut-badge {
    display: inline-flex;
    align-items: center;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 700;
    white-space: nowrap;
}

/* "gap" n'est pas supporte par le rendu canvas d'html2canvas : marge
   explicite sur l'icone pour separer icone et texte dans l'image capturee. */
.statut-badge i {
    margin-right: 6px;
}

.statut-badge-ok {
    color: #155724;
    background-color: #d4edda;
    border: 1px solid #c3e6cb;
}
.statut-badge-ok .fa {
    color: #2e7d32;
}

.statut-badge-vide {
    color: #dc3545;
    background-color: #f8d7da;
    border: 1px solid #f5c6cb;
}
.statut-badge-vide .fa {
    color: #dc3545;
}

.is_en_cours {
    background: rgba(42, 153, 241, 0.42) !important;
}
.is_en_cours::before {
    background: linear-gradient(90deg, rgba(42, 153, 241, 1), rgba(42, 153, 241, 1)) !important;
}
.is_en_cours .detail-label {
    color: #585a5b !important;
}

.is_green {
    background: #def6e4;
}

.is_pending {
    background: #eef1f5;
}
.is_pending::before {
    background: linear-gradient(90deg, #6c757d, #adb5bd) !important;
}
.is_pending .detail-label {
    color: #6c757d !important;
}
</style>
